import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { Box, Divider, Typography } from '@mui/material';
import { AxiosError } from 'axios';
import { FormikHelpers, FormikProvider, useFormik } from 'formik';
import {
  filter,
  find,
  includes,
  isEmpty,
  map,
  some,
  toUpper,
  values,
} from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import * as Yup from 'yup';

import {
  currencyExchangeApi,
  ordersApi,
  requisitesApi,
  shopsApi,
  tradersApi,
} from 'api';
import {
  CloseFormikDialogResult,
  CopyText,
  Dialog,
  DialogProps,
  FormControls,
  FormikNumericField,
  FormikSelect,
  FormikSelectOption,
  FormikTextField,
  RequisitesSelect,
  ShopSelect,
  TraderSelect,
} from 'components';
import { P2PProviderType, QueryKey, WebhookMethod } from 'enums';
import {
  useCurrencies,
  useMutation,
  useShopsQuery,
  useUser,
  useUserContext,
  useUserContextUtils,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { CreateOrderDto } from 'types';
import { formUtils, orderUtils } from 'utils';

type Values = CreateOrderDto;

type Props = DialogProps;

// Backend PublicApiErrorCode.ExternalOrderDublicate — a merchant order id already exists (including a
// previously declined attempt that was kept as a cancelled order).
// TODO: get rid of error code exceptions in platform backend
const EXTERNAL_ORDER_DUPLICATE_ERROR_CODE = 'O10006';

export const CreateOrderDialog: React.FC<Props> = ({ open, onClose }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.create_order_dialog',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { tradeMethods, paymentTypes, banks, p2pProviders } = useUserContext();
  const { getPaymentTypesOptions, getNotArchived } = useUserContextUtils();
  const { role } = useUser();
  const queryClient = useQueryClient();
  const { fiatCurrenciesOptions } = useCurrencies();
  const [mappedFiatCurrenciesOptions, setMappedFiatCurrenciesOptions] =
    useState<FormikSelectOption[]>([]);
  const [createResult, setCreateResult] = useState<{
    open: boolean;
    success: boolean;
    orderId?: string;
    message?: string;
  }>({ open: false, success: false });

  const initialValues: Values = useMemo(
    () => ({
      amount: 0,
      fiatCurrencyId: '',
      shopId: '',
      traderId: '',
      requisitesId: '',
      providerId: '',
      paymentTypeId: '',
      bankId: '',
      customer: {
        id: '',
        name: '',
        email: '',
        phone: '',
      },
      integration: {
        externalOrderId: '',
        callbackUrl: '',
        callbackMethod: '',
      },
    }),
    [],
  );

  const queryResultShops = useShopsQuery(
    QueryKey.Shops,
    shopsApi.getAllAsRole(role),
  );
  const queryResultTraders = useQuery(QueryKey.Traders, () =>
    tradersApi.getAllAsRole(role)(),
  );
  const queryResultCurrencyExchange = useQuery(
    QueryKey.CurrencyExchangeRates,
    () => currencyExchangeApi.getRates(),
  );

  const { mutate: create } = useMutation(ordersApi.createAsRole(role), {
    // The error is shown in the dedicated result dialog, so suppress the global error toast.
    mergeNotifierMessages: { error: null },
    onError: (error: AxiosError) => {
      const data = error.response?.data as
        | { orderId?: string; errorCode?: string }
        | undefined;
      const formErrors = orderUtils.getFormErrors(error);
      // A duplicate merchant order id arrives as an error code (not a field error), so highlight the
      // field explicitly; otherwise map the class-validator field errors as usual.
      const isExternalOrderDuplicate =
        data?.errorCode === EXTERNAL_ORDER_DUPLICATE_ERROR_CODE;
      if (isExternalOrderDuplicate) {
        formik.setFieldError(
          'integration.externalOrderId',
          tCommon('errors.external_order_duplicate'),
        );
      } else {
        formik.setErrors(formErrors);
      }
      // A provider decline returns the cancelled order id; show it with the selected provider name.
      const orderId = data?.orderId;
      const providerName = find(p2pProviders, {
        id: formik.values.providerId,
      })?.name;
      const message =
        orderId && providerName
          ? t('result.declined_message', { name: providerName })
          : isExternalOrderDuplicate
          ? tCommon('errors.external_order_duplicate')
          : (formErrors.providerId as string) || tCommon('errors.invalid');
      setCreateResult({ open: true, success: false, orderId, message });
    },
  });

  const handleClose = useCallback(
    (result: CloseFormikDialogResult<Values>) => {
      if (!result.ok) {
        result?.data?.formikHelpers.resetForm();
        onClose(result);
      } else if (result.data?.values) {
        create(result.data?.values, {
          onSuccess: (order) => {
            queryClient.invalidateQueries(QueryKey.TraderDisputeOrders);
            // A provider-based order lands in customer_confirm (Active tab), not a dispute.
            queryClient.invalidateQueries(QueryKey.ActiveOrders);
            result?.data?.formikHelpers.resetForm();
            onClose(result);
            setCreateResult({
              open: true,
              success: true,
              orderId: order?.id,
              message: t('result.success_message'),
            });
          },
        });
      }
    },
    [queryClient, create, onClose, t],
  );

  const handleSubmit = useCallback(
    (values: Values, formikHelpers: FormikHelpers<Values>) => {
      handleClose({ ok: true, data: { values, formikHelpers } });
    },
    [handleClose],
  );

  const handleResultClose = useCallback(() => {
    setCreateResult({ open: false, success: false });
  }, []);

  const validationSchema: Yup.ObjectSchema<Partial<Values>> = useMemo(
    () =>
      Yup.object().shape({
        amount: Yup.number()
          .moreThan(0, tCommon('errors.natural_number'))
          .required(tCommon('errors.required')),
        fiatCurrencyId: Yup.string().required(tCommon('errors.required')),
        shopId: Yup.string().required(tCommon('errors.required')),
        providerId: Yup.string(),
        traderId: Yup.string().when('providerId', {
          is: (providerId: string) => !providerId,
          then: (schema) => schema.required(tCommon('errors.required')),
        }),
        requisitesId: Yup.string().when('providerId', {
          is: (providerId: string) => !providerId,
          then: (schema) => schema.required(tCommon('errors.required')),
        }),
        paymentTypeId: Yup.string().when('providerId', {
          is: (providerId: string) => !!providerId,
          then: (schema) => schema.required(tCommon('errors.required')),
        }),
        bankId: Yup.string().when('providerId', {
          is: (providerId: string) =>
            find(p2pProviders, { id: providerId })?.type ===
            P2PProviderType.ManualResolve,
          then: (schema) => schema.required(tCommon('errors.required')),
        }),
        customer: Yup.object().shape({
          id: Yup.string().required(tCommon('errors.required')),
          name: Yup.string(),
          email: Yup.string(),
          phone: Yup.string(),
        }),
        integration: Yup.object().shape({
          externalOrderId: Yup.string(),
          callbackUrl: Yup.string(),
          callbackMethod: Yup.string().is(values(WebhookMethod)),
        }),
      }),
    [tCommon, p2pProviders],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    validateOnMount: true,
    onSubmit: handleSubmit,
    isInitialValid: false,
  });

  const isProviderMode = !!formik.values.providerId;
  const isManualResolveProvider = useMemo(
    () =>
      find(p2pProviders, { id: formik.values.providerId })?.type ===
      P2PProviderType.ManualResolve,
    [p2pProviders, formik.values.providerId],
  );

  const queryResultRequisites = useQuery(
    [QueryKey.Requisites, formik.values.traderId],
    () =>
      requisitesApi.getAllForTraderAsRole(role)(formik.values.traderId || ''),
    { enabled: !!formik.values.traderId },
  );

  // Any provider is selectable; the backend validates support, so the list isn't pre-filtered.
  const providerOptions = useMemo(
    () =>
      formUtils.getOptions(
        filter(p2pProviders, (provider) => !provider.archived),
      ),
    [p2pProviders],
  );

  const { data: shopTradeMethodIds } = useQuery(
    [QueryKey.ShopPayinTradeMethods, formik.values.shopId],
    () => shopsApi.getPayinTradeMethodIds(formik.values.shopId),
    { enabled: open && !!formik.values.shopId },
  );

  const shopTradeMethods = useMemo(
    () =>
      filter(
        tradeMethods,
        (tradeMethod) =>
          includes(shopTradeMethodIds, tradeMethod.id) &&
          tradeMethod.fiatCurrencyId === formik.values.fiatCurrencyId,
      ),
    [tradeMethods, shopTradeMethodIds, formik.values.fiatCurrencyId],
  );

  const paymentTypesOptions = useMemo(() => {
    const allowedPaymentTypeIds = map(shopTradeMethods, 'paymentTypeId');
    return getPaymentTypesOptions(
      filter(paymentTypes, (paymentType) =>
        includes(allowedPaymentTypeIds, paymentType.id),
      ),
    );
  }, [paymentTypes, shopTradeMethods, getPaymentTypesOptions]);

  const bankOptions = useMemo(() => {
    const allowedBankIds = map(
      filter(
        shopTradeMethods,
        (tradeMethod) =>
          !formik.values.paymentTypeId ||
          tradeMethod.paymentTypeId === formik.values.paymentTypeId,
      ),
      'bankId',
    );
    return formUtils.getOptions(
      filter(getNotArchived(banks), (bank) =>
        includes(allowedBankIds, bank.id),
      ),
    );
  }, [banks, shopTradeMethods, getNotArchived, formik.values.paymentTypeId]);

  const handleShopSelectChange = useCallback(
    (newShopId: string) => {
      const shop = find(queryResultShops.data, { id: newShopId });
      const shopAssetCurrencyId = shop?.assetCurrencyId;
      const shopFiatCurrencyId = shop?.fiatCurrencyId;
      const newMappedFiatCurrenciesOptions = shopFiatCurrencyId
        ? filter(fiatCurrenciesOptions, { value: shopFiatCurrencyId })
        : filter(
            fiatCurrenciesOptions,
            (option) =>
              some(tradeMethods, { fiatCurrencyId: option.value }) &&
              some(queryResultCurrencyExchange.data, {
                fiatCurrencyId: option.value,
                assetCurrencyId: shopAssetCurrencyId,
              }),
          );
      setMappedFiatCurrenciesOptions(newMappedFiatCurrenciesOptions);

      formik.setValues((prevValues) => ({
        ...prevValues,
        shopId: newShopId,
        providerId: '',
        paymentTypeId: '',
        bankId: '',
        ...(isEmpty(newMappedFiatCurrenciesOptions) && { fiatCurrencyId: '' }),
        ...(newMappedFiatCurrenciesOptions.length === 1 && {
          fiatCurrencyId: newMappedFiatCurrenciesOptions[0]?.value,
        }),
      }));
    },
    [
      formik,
      fiatCurrenciesOptions,
      queryResultCurrencyExchange.data,
      queryResultShops.data,
      tradeMethods,
    ],
  );

  // One `setValues` instead of per-field `setFieldValue` calls: each of those revalidates against the
  // values snapshot of the current render, so the last one would leave `isValid` computed on the
  // pre-reset values and keep the OK button enabled with the cleared fields (SPAY-4837).
  const handleProviderSelectChange = useCallback(
    (newProviderId: string) => {
      formik.setValues((prevValues) => ({
        ...prevValues,
        providerId: newProviderId,
        traderId: '',
        requisitesId: '',
        paymentTypeId: '',
        bankId: '',
      }));
    },
    [formik],
  );

  const isFiatSelectDisabled = useMemo(
    () => mappedFiatCurrenciesOptions.length === 1,
    [mappedFiatCurrenciesOptions],
  );

  return (
    <FormikProvider value={formik}>
      <Dialog
        open={open}
        title={t('title')}
        data={{ values: formik.values, formikHelpers: formik }}
        okDisabled={!formik.isValid}
        onClose={handleClose}
      >
        <FormControls>
          <Typography variant="subtitle1">
            {t('sections.order_info')}
          </Typography>
          <ShopSelect
            name="shopId"
            shops={queryResultShops.data}
            onChange={handleShopSelectChange}
            required
          />
          <FormControls row>
            <FormikNumericField
              label={t('fields.amount')}
              name="amount"
              required
              fullWidth
              allowNegative={false}
            />
            <FormikSelect
              label={t('fields.currency')}
              name="fiatCurrencyId"
              required
              fullWidth
              options={mappedFiatCurrenciesOptions}
              noneOption
              disabled={isFiatSelectDisabled}
            />
          </FormControls>
        </FormControls>
        <Divider sx={{ margin: '24px 0 12px' }} />
        <FormControls>
          <Typography variant="subtitle1">
            {t('sections.recipient_info')}
          </Typography>
          <FormikSelect
            name="providerId"
            label={t('fields.provider')}
            options={providerOptions}
            noneOption
            disabled={!formik.values.shopId}
            onChange={handleProviderSelectChange}
          />
          {isProviderMode ? (
            <Fragment>
              <FormikSelect
                name="paymentTypeId"
                label={t('fields.payment_type')}
                options={paymentTypesOptions}
                noneOption
                required
              />
              <FormikSelect
                name="bankId"
                label={t('fields.bank')}
                options={bankOptions}
                noneOption
                required={isManualResolveProvider}
              />
            </Fragment>
          ) : (
            <Fragment>
              <TraderSelect
                name="traderId"
                required
                traders={queryResultTraders.data}
                onChange={() => {
                  formik.setFieldValue('requisitesId', '');
                }}
              />
              <RequisitesSelect
                name="requisitesId"
                disabled={!formik.values.traderId}
                required
                requisites={queryResultRequisites.data}
                labelClassName="tw-max-w-[400px] tw-truncate"
                fiatCurrencyId={formik.values.fiatCurrencyId}
                crossFiatCurrencyIds={find(queryResultShops.data, {
                  id: formik.values.shopId,
                })?.payinFiatCurrencyExchangeConfigs?.map(
                  (config) => config.fiatCurrencyExchange.crossFiatCurrencyId,
                )}
              />
            </Fragment>
          )}
        </FormControls>
        <Divider sx={{ margin: '24px 0 12px' }} />
        <FormControls>
          <Typography variant="subtitle1">
            {t('sections.client_info')}
          </Typography>
          <FormikTextField name="customer.id" label={t('fields.id')} required />
          <FormikTextField name="customer.name" label={t('fields.name')} />
          <FormikTextField name="customer.email" label={t('fields.email')} />
          <FormikTextField name="customer.phone" label={t('fields.phone')} />
        </FormControls>
        <Divider sx={{ margin: '24px 0 12px' }} />
        <FormControls>
          <Typography variant="subtitle1">
            {t('sections.integration')}
          </Typography>
          <FormikTextField
            name="integration.externalOrderId"
            label={t('fields.external_order_id')}
          />
          <FormikTextField
            name="integration.callbackUrl"
            label={t('fields.callback_url')}
          />
          <FormikSelect
            name="integration.callbackMethod"
            label={t('fields.callback_method')}
            noneOption
            options={map(values(WebhookMethod), (method) => ({
              label: toUpper(method),
              value: method,
            }))}
          />
        </FormControls>
      </Dialog>
      <Dialog
        open={createResult.open}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {createResult.success ? (
              <CheckCircleIcon color="success" />
            ) : (
              <ErrorIcon color="error" />
            )}
            {createResult.success
              ? t('result.success_title')
              : createResult.orderId
              ? t('result.error_provider_title')
              : t('result.error_title')}
          </Box>
        }
        hideCancel
        dialogProps={{
          open: createResult.open,
          PaperProps: { sx: { maxWidth: 400 } },
        }}
        onClose={handleResultClose}
      >
        {createResult.message && (
          <Typography>{createResult.message}</Typography>
        )}
        {createResult.orderId && (
          <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography>{t('result.order_id')}</Typography>
            <CopyText text={createResult.orderId} />
          </Box>
        )}
      </Dialog>
    </FormikProvider>
  );
};
